import { useState } from "react";
import { Mail, Phone, MessageCircle, Clock, User } from "lucide-react";
import { PageHero } from "@/components/Section";
import Reveal from "@/components/Reveal";
import CTAButton from "@/components/CTAButton";
import { Field, TextInput, SelectInput, TextArea, FormNotice } from "@/components/FormControls";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";

const empty = { name: "", email: "", phone: "", topic: "", message: "" };

export default function ContactPage() {
  const brand = useBrand();
  const [form, setForm] = useState(empty);
  const [state, setState] = useState({ status: "idle", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/leads/contact", form);
      setState({ status: "done", message: data.message });
      setForm(empty);
    } catch (err) {
      setState({
        status: "error",
        message: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  const details = [
    { icon: User, label: "Speak to", value: brand.contact.name },
    { icon: Mail, label: "Email", value: brand.contact.email, href: `mailto:${brand.contact.email}` },
    { icon: Phone, label: "Phone", value: brand.contact.phone, href: `tel:${brand.contact.phone.replace(/\s/g, "")}` },
    {
      icon: MessageCircle,
      label: "WhatsApp",
      value: "Message us",
      href: `https://wa.me/${brand.contact.whatsapp}`,
    },
    { icon: Clock, label: "Hours", value: brand.contact.hours },
  ];

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Talk to a person, not a form letter."
        intro={`We are based in ${brand.serviceAreas.cities.join(", ")}. If your child has a severe allergy, please speak to us before ordering.`}
      />

      <section className="py-14 sm:py-20">
        <div className="mbm-container grid gap-12 lg:grid-cols-[0.8fr_1fr]">
          <Reveal>
            <ul className="space-y-4">
              {details.map((d) => (
                <li key={d.label} className="flex gap-4 rounded-card bg-surface shadow-soft p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-creamDeep text-coralInk">
                    <d.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cocoa/70">
                      {d.label}
                    </p>
                    {d.href ? (
                      <a
                        href={d.href}
                        target={d.href.startsWith("http") ? "_blank" : undefined}
                        rel="noreferrer"
                        data-testid={`contact-info-${d.label.toLowerCase()}`}
                        className="mt-1 block font-heading text-sm font-semibold text-coralInk hover:text-rust"
                      >
                        {d.value}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm font-medium text-cocoa">{d.value}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-card bg-surface shadow-soft p-5 text-sm leading-relaxed text-cocoa/70">
              <p className="font-heading font-semibold text-cocoa">{brand.contact.fssai}</p>
              <p className="mt-1">{brand.contact.gstin}</p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <form onSubmit={submit} className="mbm-card p-6 sm:p-8" data-testid="contact-form">
              <h2 className="font-heading text-lg font-semibold">Send us a message</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <Field label="Your name" required>
                  <TextInput required value={form.name} onChange={set("name")} data-testid="contact-name" />
                </Field>
                <Field label="Email" required>
                  <TextInput required type="email" value={form.email} onChange={set("email")} data-testid="contact-email" />
                </Field>
                <Field label="Phone">
                  <TextInput type="tel" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
                </Field>
                <Field label="What is it about?">
                  <SelectInput value={form.topic} onChange={set("topic")} data-testid="contact-topic">
                    <option value="">Select</option>
                    <option value="plans">Plans and pricing</option>
                    <option value="allergy">Allergy or dietary question</option>
                    <option value="delivery">Delivery or service area</option>
                    <option value="school">School delivery</option>
                    <option value="order">Order support</option>
                    <option value="work">Working with us</option>
                    <option value="other">Something else</option>
                  </SelectInput>
                </Field>
                <Field label="Message" required className="sm:col-span-2">
                  <TextArea required value={form.message} onChange={set("message")} data-testid="contact-message" />
                </Field>
              </div>
              {state.message && (
                <div className="mt-5">
                  <FormNotice tone={state.status === "error" ? "error" : "info"} testId="contact-result">
                    {state.message}
                  </FormNotice>
                </div>
              )}
              <CTAButton
                type="submit"
                className="mt-6 w-full"
                disabled={state.status === "loading"}
                data-testid="contact-submit"
              >
                {state.status === "loading" ? "Sending…" : "Send message"}
              </CTAButton>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
